import { useEffect, useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import logo from "../../../../assets/logo-nanthiat.png";

export default function FamilleInteractions({ famille }) {
  const [showForm, setShowForm] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    typeInteraction: "",
    canal: "",
    date: "",
    contenu: "",
    prochaineEtape: "",
    dateProchaineInteraction: "",
    important: false,
    commentaireInterne: "",
  });

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";
      
  useEffect(() => {
    if (famille?._id) fetchInteractions();
  }, [famille]);

  const fetchInteractions = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/interactions/${famille._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setInteractions(sorted);
    } catch (err) {
      console.error("Erreur récupération interactions :", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddInteraction = async () => {
    const { typeInteraction, canal, date, contenu } = formData;
    if (!typeInteraction || !canal || !date || !contenu) {
      setErrorMessage("Tous les champs obligatoires doivent être remplis.");
      return;
    }
  
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BACKEND_URL}/interactions`,
        {
          ...formData,
          prospectId: famille._id,
          referent: famille.referent,
          statut: "Client",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      fetchInteractions();
      setShowForm(false);
      setFormData({
        typeInteraction: "",
        canal: "",
        date: "",
        contenu: "",
        prochaineEtape: "",
        dateProchaineInteraction: "",
        important: false,
        commentaireInterne: "",
      });
      setErrorMessage(""); // reset error
    } catch (err) {
      console.error("Erreur ajout interaction :", err);
      setErrorMessage("Erreur lors de l'ajout de l'interaction.");
    }
  };    

  const handleDelete = async (id) => {
    const confirm = window.confirm("Supprimer cette interaction ?");
    if (!confirm) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_URL}/interactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchInteractions();
    } catch (err) {
      console.error("Erreur suppression :", err);
    }
  };

  if (!famille) {
    return (
      <div className="flex flex-col items-center justify-center text-center bg-white shadow-md rounded-lg h-[300px] w-full p-6 space-y-4">
        <img src={logo} alt="Logo Nanthiat" className="w-20 h-20" />
        <p className="text-gray-500">
          Sélectionnez une famille pour voir son historique d’interactions.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 w-full flex flex-col justify-between h-[300px]">
      <div className="flex-1 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Dernières interactions</h2>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
          >
            <PlusIcon className="w-5 h-5" />
            Ajouter
          </button>
        </div>

        {interactions.length > 0 ? (
          <div className="space-y-3 pr-1 max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          {interactions.map((item) => (
            <div
              key={item._id}
              className="relative border-l-4 border-blue-500 pl-4 pr-10 py-3 bg-gray-50 rounded-md shadow-sm"
            >
              {/* Icône important animée */}
              {item.important && (
                <div className="absolute top-2 right-2 text-yellow-500 animate-pulse">
                  <ExclamationTriangleIcon className="w-8 h-8" title="Important" />
                </div>
              )}
        
              <p className="text-sm text-gray-800 font-medium mb-1">
                {item.typeInteraction} via {item.canal} –{" "}
                {new Date(item.date).toLocaleDateString()}
              </p>
        
              <p className="text-sm text-gray-600">{item.contenu}</p>
        
              {item.prochaineEtape && (
                <p className="text-sm text-blue-600 mt-1">
                  📌 {item.prochaineEtape}{" "}
                  {item.dateProchaineInteraction &&
                    `(le ${new Date(item.dateProchaineInteraction).toLocaleDateString()})`}
                </p>
              )}
        
              {/* Corbeille en bas à droite */}
              <button
                onClick={() => handleDelete(item._id)}
                className="absolute bottom-2 right-2 text-red-600 hover:text-red-800"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>        
        ) : (
          <p className="text-sm text-gray-500">Aucune interaction récente</p>
        )}
      </div>

      {/* Modal ajout d’interaction (identique à Prospect) */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Nouvelle interaction</h3> 
            {errorMessage && (
              <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">
                {errorMessage}
              </div>
            )}
     
            {/* 🧾 Contenu du formulaire */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* Type d'interaction */}
              <div className="col-span-1">
                <label className="block text-sm font-medium">Type d'interaction *</label>
                <select
                  name="typeInteraction"
                  value={formData.typeInteraction}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">-- Sélectionner --</option>
                  {["Appel", "Email", "Rendez-vous", "Message LinkedIn", "Note interne", "Autre"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Canal */}
              <div className="col-span-1">
                <label className="block text-sm font-medium">Canal *</label>
                <select
                  name="canal"
                  value={formData.canal}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">-- Sélectionner --</option>
                  {["Téléphone", "Email", "LinkedIn", "Visio", "WhatsApp", "Autre"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div className="col-span-1">
                <label className="block text-sm font-medium">Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              {/* Prochaine étape */}
              <div className="col-span-1">
                <label className="block text-sm font-medium">Prochaine étape</label>
                <input
                  name="prochaineEtape"
                  value={formData.prochaineEtape}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              {/* Date prochaine interaction */}
              <div className="col-span-1">
                <label className="block text-sm font-medium">Date prochaine interaction</label>
                <input
                  type="date"
                  name="dateProchaineInteraction"
                  value={formData.dateProchaineInteraction}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              {/* Important */}
              <div className="col-span-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  name="important"
                  checked={formData.important}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600"
                />
                <label className="text-sm">Marquer comme important</label>
              </div>

              {/* Contenu */}
              <div className="col-span-2">
                <label className="block text-sm font-medium">Contenu *</label>
                <textarea
                  name="contenu"
                  value={formData.contenu}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              {/* Commentaire interne */}
              <div className="col-span-2">
                <label className="block text-sm font-medium">Commentaire interne</label>
                <textarea
                  name="commentaireInterne"
                  value={formData.commentaireInterne || ""}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
            </div>

            {/* Boutons d’action */}
            <div className="flex justify-end gap-2 pt-6">
              <button
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
                onClick={() => setShowForm(false)}
              >
                Annuler
              </button>
              <button
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                onClick={handleAddInteraction}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}